import type { TrainingPlan, TrainingPlanSession, WeeklyPlanWeek } from '@/types/curriculum';
import type { ProgramDrill, TrainingProgram } from '@/types/training-program';
import type { TrainingDrill } from '@/lib/training-program/drill-loader';

type SessionMapEntry = {
  session: TrainingPlanSession;
  week: WeeklyPlanWeek;
};

export const getCurrentWeekIndex = (plan: TrainingPlan): number => {
  const startDate = new Date(plan.created_at);
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.min(Math.max(Math.ceil(diffDays / 7) - 1, 0), plan.weeks.length - 1);
};

export const buildSessionMap = (plan: TrainingPlan): Record<string, SessionMapEntry> => {
  return plan.weeks.reduce<Record<string, SessionMapEntry>>((acc, week) => {
    week.sessions.forEach((session) => {
      if (!session.drill_id) return;
      acc[session.drill_id] = { session, week };
    });
    return acc;
  }, {});
};

const formatAssignedReps = (session?: TrainingPlanSession): ProgramDrill['assigned_reps'] => {
  if (!session) {
    return { sets: 0, reps_per_set: 0, rest_seconds: 0 };
  }
  return {
    sets: 1,
    reps_per_set: session.target_reps || 0,
    rest_seconds: 0,
  };
};

export const mapTrainingDrillToProgram = (
  drill: TrainingDrill,
  options: { isRequired: boolean; session?: TrainingPlanSession }
): ProgramDrill => {
  return {
    drill_id: drill.drill_id,
    drill_name: drill.drill_name,
    focus_area: drill.focus_area,
    why_this_helps: drill.program_connection_text,
    what_you_are_training: drill.focus_area,
    how_to_perform: drill.steps,
    swing_thought: drill.swing_thought,
    assigned_reps: formatAssignedReps(options.session),
    what_to_look_for: drill.what_to_look_for,
    common_mistakes: drill.common_mistakes,
    video_url: drill.video_url,
    is_required: options.isRequired,
    category: drill.category ?? null,
  };
};

export const buildTrainingProgram = (params: {
  plan: TrainingPlan;
  requiredDrills: ProgramDrill[];
  optionalDrills: ProgramDrill[];
  currentWeekIndex: number;
}): TrainingProgram => {
  const { plan, requiredDrills, optionalDrills, currentWeekIndex } = params;
  const currentWeek = plan.weeks[currentWeekIndex] ?? plan.weeks[0];
  const programTitle =
    plan.primary_track?.display_name ||
    currentWeek?.focus_fault_names?.[0] ||
    'Training Program';
  const estimatedTimeMinutes = currentWeek
    ? currentWeek.sessions.reduce((total, session) => total + (session.duration_minutes || 0), 0)
    : 0;

  return {
    program_id: plan.id,
    program_title: programTitle,
    primary_fault: currentWeek?.focus_fault_names?.[0] || plan.primary_fault_key || 'Primary Focus',
    required_drills: requiredDrills,
    optional_drills: optionalDrills,
    estimated_time_minutes: estimatedTimeMinutes,
    created_at: plan.created_at,
  };
};
